// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Shield, Loader2, AlertCircle, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { VCIN_COLORS } from '../styles/constants';

export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const { signIn, setActive, isLoaded } = useSignIn();
  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    setError(null);

    try {
      await signIn.create({ strategy: 'reset_password_email_code', identifier: email });
      setStep('code');
    } catch (err) {
      // Always show code step to prevent email enumeration
      setStep('code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;

    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        setSuccess(true);
      } else {
        setError('Reset failed. Please try again.');
      }
    } catch (err) {
      const e0 = err.errors?.[0];
      setError(e0?.longMessage || e0?.message || 'Invalid code or reset failed.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-vcin-bg flex flex-col items-center justify-center px-6" data-testid="forgot-password-success">
        {/* Logo Section */}
        <div className="mb-10 text-center">
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ backgroundColor: VCIN_COLORS.primary }}
          >
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-vcin-text tracking-tight">VCIN</h1>
          <p className="text-vcin-secondary text-sm mt-1">Vehicle Crime Intelligence Network</p>
        </div>

        {/* Success Message */}
        <div className="w-full max-w-sm">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Password Reset Successfully</h2>
            <p className="text-sm text-gray-600 mb-4">
              Your password has been updated and you are now signed in.
            </p>
          </div>

          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-6 w-full h-12 text-white font-semibold rounded-xl flex items-center justify-center shadow-lg"
            style={{ backgroundColor: VCIN_COLORS.primary }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vcin-bg flex flex-col items-center justify-center px-6" data-testid="forgot-password-screen">
      {/* Back Button */}
      <button
        onClick={() => step === 'code' ? setStep('email') : navigate(-1)}
        className="absolute top-6 left-6 p-2 rounded-xl border bg-white hover:bg-gray-50"
        style={{ borderColor: '#D8DCE4' }}
      >
        <ArrowLeft className="w-5 h-5 text-gray-600" />
      </button>

      {/* Logo Section */}
      <div className="mb-10 text-center">
        <div
          className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg"
          style={{ backgroundColor: VCIN_COLORS.primary }}
        >
          <Shield className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-vcin-text tracking-tight">VCIN</h1>
        <p className="text-vcin-secondary text-sm mt-1">Vehicle Crime Intelligence Network</p>
      </div>

      {/* Form */}
      <div className="w-full max-w-sm">
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
          {step === 'email' ? 'Forgot Password?' : 'Reset Your Password'}
        </h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          {step === 'email'
            ? "Enter your email address and we'll send you a reset code."
            : `Enter the 6-digit code sent to ${email} and choose a new password.`}
        </p>

        <form onSubmit={step === 'email' ? handleSendCode : handleResetPassword} className="space-y-5">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-vcin-alert-red flex-shrink-0 mt-0.5" />
              <p className="text-vcin-alert-red text-sm">{error}</p>
            </div>
          )}

          {step === 'email' && (
            <div>
              <label htmlFor="email" className="block text-vcin-secondary text-sm mb-2 font-medium">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
                autoFocus
                className="w-full h-14 px-4 bg-white border rounded-xl text-vcin-text placeholder:text-vcin-secondary/50 focus:outline-none focus:ring-2 transition-colors shadow-sm"
                style={{ borderColor: '#D8DCE4' }}
                onFocus={(e) => e.target.style.borderColor = VCIN_COLORS.primary}
                onBlur={(e) => e.target.style.borderColor = '#D8DCE4'}
                data-testid="forgot-password-email-input"
              />
            </div>
          )}

          {step === 'code' && (
            <>
              <div>
                <label htmlFor="code" className="block text-vcin-secondary text-sm mb-2 font-medium">
                  Reset Code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  required
                  autoFocus
                  className="w-full h-14 px-4 bg-white border rounded-xl text-vcin-text placeholder:text-vcin-secondary/50 focus:outline-none focus:ring-2 transition-colors shadow-sm text-center text-lg tracking-widest"
                  style={{ borderColor: '#D8DCE4' }}
                  data-testid="forgot-password-code-input"
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-vcin-secondary text-sm mb-2 font-medium">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    className="w-full h-14 px-4 pr-12 bg-white border rounded-xl text-vcin-text placeholder:text-vcin-secondary/50 focus:outline-none focus:ring-2 transition-colors shadow-sm"
                    style={{ borderColor: '#D8DCE4' }}
                    data-testid="forgot-password-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || (step === 'email' && !email)}
            className="w-full h-14 text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-all shadow-lg"
            style={{ backgroundColor: VCIN_COLORS.primary }}
            data-testid="forgot-password-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{step === 'email' ? 'Sending...' : 'Resetting...'}</span>
              </>
            ) : (
              <span>{step === 'email' ? 'Send Reset Code' : 'Reset Password'}</span>
            )}
          </button>
        </form>

        <Link
          to="/login"
          className="mt-4 w-full h-12 border rounded-xl font-medium text-gray-700 flex items-center justify-center gap-2 hover:bg-gray-50"
          style={{ borderColor: '#D8DCE4' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>
    </div>
  );
}
